import {
  STORAGE_KEYS,
  INVOICE_FORMATS,
  INVOICE_DISPLAY_OPTIONS,
  STOCK_CHECK_OPTIONS,
  ADD_TO_CART_OPTIONS,
  DEFAULT_INVOICE_ACTION_OPTIONS,
  APP_SETTING_KEYS,
  PLATFORM_WEB,
  PRINTER_TYPES,
  SYSTEM_VIEW,
} from "const";
import TerminalService from "services/TerminalService";
import generateMacId from "components/utils/generateMacId";
import * as InvoiceHelper from "helpers/InvoiceHelper";
import * as AppSettingsHelper from "helpers/AppSettingsHelper";
import ShopService from "services/ShopService";
import ShopsHelper from "helpers/ShopsHelper";
import toast from "react-hot-toast";

let cacheTerminalSettings = null;

export const getCachedTerminal = () => {
  const cached = localStorage.getItem(STORAGE_KEYS.TERMINAL);
  if (!cached) return null;

  try {
    return JSON.parse(cached);
  } catch (error) {
    console.error("Failed to parse cached terminal:", error);
    localStorage.removeItem(STORAGE_KEYS.TERMINAL);
    return null;
  }
};

export const getCurrentTerminalId = () => {
  return getCachedTerminal()?.id || "";
};

export const getCurrentTerminalName = () => {
  return getCachedTerminal()?.name || "";
};

export const getPosTerminals = async () => {
  return TerminalService.getTerminals({ type: "pos" });
};

export const parseOrderTypes = (orderTypesRaw) => {
  if (!orderTypesRaw) return [];

  return orderTypesRaw
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

export const getActiveOrderTypesData = (terminalOrderTypesRaw) => {
  const shopProfile = ShopsHelper.getCachedShopProfile();
  const shopOrderTypes = shopProfile?.config?.order_types || {};

  const activeTypeNames = parseOrderTypes(terminalOrderTypesRaw);

  const activeOrderTypes = {};

  if (activeTypeNames.length > 0) {
    activeTypeNames.forEach((name) => {
      activeOrderTypes[name] = shopOrderTypes[name] || [];
    });
  }

  return activeOrderTypes;
};

export const attachTerminal = async (terminal, force = false) => {
  if (force && terminal?.config?.allow_force_attach === false) {
    toast.error("Force attach not supported");
    throw new Error("force attach not supported");
  }

  let data = { mac_id: generateMacId(), platform: PLATFORM_WEB };

  if (force) {
    data = { ...data, force: true };
  }

  const resp = await TerminalService.attachTerminal(terminal.id, data);

  if (resp) {
    localStorage.setItem(STORAGE_KEYS.TERMINAL, JSON.stringify(resp));
  }

  localStorage.setItem(STORAGE_KEYS.TERMINAL_AUTH_TOKEN, resp?.auth_token);
  localStorage.removeItem(STORAGE_KEYS.USER_AUTH_TOKEN);
  localStorage.setItem(STORAGE_KEYS.TERMINAL_ID, terminal.id);
  if (terminal?.name) {
    localStorage.setItem(STORAGE_KEYS.TERMINAL_NAME, terminal.name);
  }

  saveTerminalSettings({
    ...buildTerminalSettingsMap(resp, terminal.id),
    billNumberPrefix: terminal?.invoice_no_prefix || "",
    numberOfShiftsToBeStoredOnDevice: resp?.number_of_shifts_to_be_stored_on_device || 1,
  });

  if (terminal?.invoice_no_prefix) {
    await AppSettingsHelper.saveSetting(APP_SETTING_KEYS.BILL_NUMBER_PREFIX, terminal.invoice_no_prefix);
  } else {
    await AppSettingsHelper.deleteSetting(APP_SETTING_KEYS.BILL_NUMBER_PREFIX);
  }

  // Clear stale bill number and estimate suffix so that the newly attached terminal goes through bill number setup
  await AppSettingsHelper.deleteSetting(APP_SETTING_KEYS.NEXT_BILL_NO);
  localStorage.removeItem(STORAGE_KEYS.ESTIMATE_BILL_NUMBER_SUFFIX);

  await InvoiceHelper.loadInvoiceFormatForTerminal(terminal.id);
  return resp;
};

export const buildTerminalSettingsMap = (resp, terminalId) => {
  const currentSettings = getTerminalSettings();
  return {
    isOrderDashboardEnabled: !!resp?.is_order_dashboard_enabled,
    isTableDashboardEnabled:
      !!resp?.is_table_dashboard_enabled && ShopsHelper.hasFullKotValidity(ShopsHelper.getCachedShopProfile()),
    terminalId: terminalId,
    salespersonLogin: resp?.salesperson_login ?? true,
    billNumberPrefix: resp?.invoice_no_prefix || "",
    invoicePrinterFormat: resp?.invoice_format || currentSettings?.invoicePrinterFormat,
    printerType: resp?.printer_type || currentSettings?.printerType,
    printer: currentSettings?.printer || resp?.printer_id || "",
    invoice_printer_ip: resp?.invoice_printer_ip || currentSettings?.invoice_printer_ip || "",
    showTokenNumber: !!resp?.show_token_number,
    displayAwaitingDispatchOrders: !!resp?.config?.display_awaiting_dispatch_orders,
    showPaymentPage: resp?.show_payment_page,
    localTokenNumber: getLocalTokenNumber(),
    amountWithThreeFraction: !!resp?.amount_with_three_fraction,
    printMrpOnInvoice: !!resp?.show_mrp_column_on_invoice,
    printHsnOnInvoice: !!resp?.config?.print_hsn,
    paymentModeOnInvoice: !!resp?.show_payment_mode_on_invoice,
    printTotalSave: !!resp?.print_total_save_on_invoice,
    counterFoil: !!resp?.counter_foil_with_invoice,
    advancedQty: resp?.advanced_quantity_control,
    tableSelection: resp?.enable_table_selection_pos,
    kotWithInvoice: !!resp?.kot_with_bill,
    kotWithInvoiceIp: resp?.kot_with_invoice_printer_ip || currentSettings?.kotWithInvoiceIp || "",
    consolidatedKOT: !!resp?.config?.consolidated_kot,
    consolidatedKOTIp: resp?.consolidated_kot_printer_ip || currentSettings?.consolidatedKOTIp || "",
    upi_id: resp?.upi_id || "",
    orderFullPayment: !!resp?.order_with_full_payment_only,
    autoReceiveFullAmount: !!resp?.config?.auto_receive_full_amount,
    showItemChargesOnInvoice: !!resp?.config?.show_item_charges_on_invoice,
    printPaymentQrOnInvoice: !!resp?.config?.print_payment_qr_on_invoice,
    showItemsOnKotWhenTableChange: !!(
      resp?.config?.show_items_on_kot_when_table_change ?? currentSettings?.showItemsOnKotWhenTableChange
    ),
    printOnPayment: !!resp?.print_invoice_on_payment_update,
    kotFormat: resp?.kitchen_kot_format || INVOICE_FORMATS.THREE_INCH,
    consolidatedKotFormat: resp?.consolidated_kot_format || INVOICE_FORMATS.THREE_INCH,
    barcodePrinterType: resp?.barcode_printer_type || currentSettings?.barcodePrinterType || "",
    barcodePrinterFormat: resp?.barcode_printer_format || currentSettings?.barcodePrinterFormat || "",
    barcodeCustomNote: resp?.barcode_custom_note || currentSettings?.barcodeCustomNote || "",
    orderTypes: getActiveOrderTypesData(resp?.order_types),
    systemType: resp?.config?.erp_dashboard_enabled ? SYSTEM_VIEW.ERP : SYSTEM_VIEW.POS,
  };
};

export const syncTerminalSettings = async (terminalId) => {
  if (!terminalId) return;

  const resp = await TerminalService.getTerminal(terminalId);

  if (resp) {
    localStorage.setItem(STORAGE_KEYS.TERMINAL, JSON.stringify(resp));
  }

  saveTerminalSettings(buildTerminalSettingsMap(resp, terminalId));
  return resp;
};

export const getTerminalSettings = () => {
  if (cacheTerminalSettings) {
    return cacheTerminalSettings;
  }
  const settingsString = localStorage.getItem(STORAGE_KEYS.TERMINAL_SETTINGS);
  const settings = settingsString ? JSON.parse(settingsString) : {};

  // Set defaults
  if (!settings.invoiceDisplayRate) {
    settings.invoiceDisplayRate = INVOICE_DISPLAY_OPTIONS.ACTUAL_RATE.value;
  }
  if (!settings.preventNegativeStock) {
    settings.preventNegativeStock = STOCK_CHECK_OPTIONS.ALLOW_NEGATIVE_STOCK.value;
  }
  if (!settings.addToCart) {
    settings.addToCart = ADD_TO_CART_OPTIONS.APPEND.value;
  }
  if (!settings.defaultInvoiceAction) {
    settings.defaultInvoiceAction = DEFAULT_INVOICE_ACTION_OPTIONS.PRINT.value;
  }
  if (settings.showItemsOnKotWhenTableChange === undefined) {
    settings.showItemsOnKotWhenTableChange = false;
  }

  cacheTerminalSettings = settings;
  return settings;
};

export const getDefaultPrinterConfig = (settings) => {
  const terminalSettings = settings;
  const printerType = terminalSettings?.printerType || PRINTER_TYPES.USB;
  const printerTarget =
    printerType === PRINTER_TYPES.LAN ? terminalSettings?.invoice_printer_ip : terminalSettings?.printer;
  return { printerType, printerTarget };
};

export const saveTerminalSettings = (newSettings = {}) => {
  try {
    const currentSettings = getTerminalSettings();

    let tokenToSave = newSettings.localTokenNumber || currentSettings.localTokenNumber;

    const showTokenActive =
      newSettings.showTokenNumber !== undefined ? newSettings.showTokenNumber : currentSettings.showTokenNumber;

    if (showTokenActive && !tokenToSave) {
      tokenToSave = 1;
    }

    const updatedSettings = {
      ...currentSettings,
      ...newSettings,
      localTokenNumber: tokenToSave || 1,
    };

    localStorage.setItem(STORAGE_KEYS.TERMINAL_SETTINGS, JSON.stringify(updatedSettings));
    cacheTerminalSettings = updatedSettings;

    return true;
  } catch (error) {
    console.error("Error saving terminal settings:", error);
    return false;
  }
};

export const updateTerminalSettings = async (newSettings) => {
  try {
    const terminalId = getCurrentTerminalId();
    if (!terminalId) throw new Error("No Terminal ID found");

    const apiPayload = {};
    if (newSettings.invoicePrinterFormat) {
      apiPayload.invoice_format = newSettings.invoicePrinterFormat;
    }
    if (newSettings.printerType) {
      apiPayload.printer_type = newSettings.printerType;
    }
    if (newSettings.invoice_printer_ip) {
      apiPayload.invoice_printer_ip = newSettings.invoice_printer_ip;
    }
    if (newSettings.billNumberPrefix) {
      apiPayload.invoice_no_prefix = newSettings.billNumberPrefix;
    }
    if (newSettings.kotFormat) {
      apiPayload.kitchen_kot_format = newSettings.kotFormat;
    }
    if (newSettings.consolidatedKotFormat) {
      apiPayload.consolidated_kot_format = newSettings.consolidatedKotFormat;
    }

    if (Object.keys(apiPayload).length > 0) {
      try {
        await TerminalService.updateTerminalConfig(terminalId, apiPayload);
      } catch (error) {
        console.error("Failed to sync terminal settings with server:", error);
      }
    }

    return saveTerminalSettings(newSettings);
  } catch (error) {
    console.error("Error in updateTerminalSettings:", error);
    throw error;
  }
};

export const loadOrderTypesFromStorage = () => {
  const terminalSettings = getTerminalSettings();
  return terminalSettings?.orderTypes ? Object.keys(terminalSettings.orderTypes) : [];
};

export const fetchPaymentModes = async ({ setLoading = () => {} } = {}) => {
  try {
    setLoading(true);

    const paymentModes = await ShopService.getPaymentModes();

    return paymentModes || [];
  } catch (error) {
    console.error("Failed to load payment modes", error);
    return [];
  } finally {
    setLoading(false);
  }
};

export const getLocalTokenNumber = () => {
  const settings = getTerminalSettings();
  return settings.localTokenNumber || 1;
};

export const saveLocalTokenNumber = (num) => {
  return saveTerminalSettings({ localTokenNumber: num });
};

export const getDefaultPrintAction = () => {
  const terminalSettings = getTerminalSettings();
  return terminalSettings?.defaultInvoiceAction || DEFAULT_INVOICE_ACTION_OPTIONS.PRINT.value;
};
